import type {
  ScheduleViewResponse,
  ScheduleViewRow,
} from '../services/viewsClient';
import type {ChartingQueue} from '../services/chartingService';

export const MOCK_EHR_CONFIG = Object.freeze({
  status: 'active',
  ehr_type: 'mock',
  timeZone: 'America/New_York',
  __mockEhr: true,
});

export const MOCK_EHR_PATIENTS = Object.freeze([
  {
    id: 'mock-maria-garcia',
    ehrSourceId: 'ECW-100245',
    ehr_source_id: 'ECW-100245',
    firstName: 'Maria',
    lastName: 'Garcia',
    displayName: 'Maria Garcia',
    fullName: 'Maria Garcia',
    patientName: 'Maria Garcia',
    dob: '1978-04-12',
    gender: 'female',
    mrn: '100245',
    accountNo: '100245',
    language: 'Spanish',
    pcp: 'Dr. Anderson',
    pcpId: 'mock-provider-anderson',
    __mockEhr: true,
  },
  {
    id: 'mock-james-wilson',
    ehrSourceId: 'ECW-100246',
    ehr_source_id: 'ECW-100246',
    firstName: 'James',
    lastName: 'Wilson',
    displayName: 'James Wilson',
    fullName: 'James Wilson',
    patientName: 'James Wilson',
    dob: '1965-09-03',
    gender: 'male',
    mrn: '100246',
    accountNo: '100246',
    language: 'English',
    pcp: 'Dr. Anderson',
    pcpId: 'mock-provider-anderson',
    __mockEhr: true,
  },
  {
    id: 'mock-carmen-ruiz',
    ehrSourceId: 'ECW-100249',
    ehr_source_id: 'ECW-100249',
    firstName: 'Carmen',
    lastName: 'Ruiz',
    displayName: 'Carmen Ruiz',
    fullName: 'Carmen Ruiz',
    patientName: 'Carmen Ruiz',
    dob: '1970-05-19',
    gender: 'female',
    mrn: '100249',
    accountNo: '100249',
    language: 'Spanish',
    pcp: 'Dr. Anderson',
    pcpId: 'mock-provider-anderson',
    __mockEhr: true,
  },
  {
    id: 'mock-sophie-dubois',
    ehrSourceId: 'ECW-100250',
    ehr_source_id: 'ECW-100250',
    firstName: 'Sophie',
    lastName: 'Dubois',
    displayName: 'Sophie Dubois',
    fullName: 'Sophie Dubois',
    patientName: 'Sophie Dubois',
    dob: '1992-01-14',
    gender: 'female',
    mrn: '100250',
    accountNo: '100250',
    language: 'French',
    pcp: 'Dr. Anderson',
    pcpId: 'mock-provider-anderson',
    __mockEhr: true,
  },
  {
    id: 'mock-daniel-kim',
    ehrSourceId: 'ECW-100251',
    ehr_source_id: 'ECW-100251',
    firstName: 'Daniel',
    lastName: 'Kim',
    displayName: 'Daniel Kim',
    fullName: 'Daniel Kim',
    patientName: 'Daniel Kim',
    dob: '1988-10-07',
    gender: 'male',
    mrn: '100251',
    accountNo: '100251',
    language: 'Korean',
    pcp: 'Dr. Anderson',
    pcpId: 'mock-provider-anderson',
    __mockEhr: true,
  },
  {
    id: 'mock-lucia-moreno',
    ehrSourceId: 'ECW-100252',
    ehr_source_id: 'ECW-100252',
    firstName: 'Lucia',
    lastName: 'Moreno',
    displayName: 'Lucia Moreno',
    fullName: 'Lucia Moreno',
    patientName: 'Lucia Moreno',
    dob: '1976-07-28',
    gender: 'female',
    mrn: '100252',
    accountNo: '100252',
    language: 'Spanish',
    pcp: 'Dr. Anderson',
    pcpId: 'mock-provider-anderson',
    __mockEhr: true,
  },
  {
    id: 'mock-amina-hassan',
    ehrSourceId: 'ECW-100247',
    ehr_source_id: 'ECW-100247',
    firstName: 'Amina',
    lastName: 'Hassan',
    displayName: 'Amina Hassan',
    fullName: 'Amina Hassan',
    patientName: 'Amina Hassan',
    dob: '1984-08-21',
    gender: 'female',
    mrn: '100247',
    accountNo: '100247',
    language: 'Arabic',
    pcp: 'Dr. Patel',
    pcpId: 'mock-provider-patel',
    __mockEhr: true,
  },
  {
    id: 'mock-wei-chen',
    ehrSourceId: 'ECW-100248',
    ehr_source_id: 'ECW-100248',
    firstName: 'Wei',
    lastName: 'Chen',
    displayName: 'Wei Chen',
    fullName: 'Wei Chen',
    patientName: 'Wei Chen',
    dob: '1959-12-02',
    gender: 'male',
    mrn: '100248',
    accountNo: '100248',
    language: 'Mandarin',
    pcp: 'Dr. Chen',
    pcpId: 'mock-provider-chen',
    __mockEhr: true,
  },
]);

export const MOCK_UNIFIED_PATIENTS = Object.freeze(
  MOCK_EHR_PATIENTS.map(patient => ({
    id: `ehr_${patient.id}`,
    localPatient: null,
    ehrPatient: patient,
    matchKind: 'ehrOnly',
    displayName: patient.displayName,
    sortName: patient.lastName.toLowerCase(),
  })),
);

export const MOCK_EHR_TRANSCRIPT = Object.freeze([
  {
    id: 'mock-transcript-1',
    speaker: 'provider',
    text: 'How have your blood sugars been since our last visit?',
    timestamp: '2026-01-15T14:00:00.000Z',
  },
  {
    id: 'mock-transcript-2',
    speaker: 'patient',
    text: 'Mostly between 140 and 180 in the morning. I have been more tired this month.',
    timestamp: '2026-01-15T14:00:08.000Z',
  },
]);

export function getMockEhrRecentEncounters(now: Date = new Date()) {
  const rows = [
    {
      id: 'mock-scribe-robert',
      patientId: 'mock-robert-mitchell',
      patientName: 'Robert Mitchell',
      language: 'English',
      summary: 'Emergency evaluation for chest pain and shortness of breath.',
      workflowType: 'medical_scribing',
      tags: ['scribe'],
      minutes: 26,
      hoursAgo: 1,
    },
    {
      id: 'mock-interpreter-maria',
      patientId: 'mock-maria-garcia',
      patientName: 'María García',
      language: 'Spanish',
      summary:
        'Patient consultation for chronic back pain and mobility issues.',
      workflowType: 'interpretation',
      tags: ['interpretation'],
      minutes: 15,
      hoursAgo: 2,
    },
    {
      id: 'mock-interpreter-wei',
      patientId: 'mock-wei-chen',
      patientName: 'Wei Chen',
      language: 'Mandarin',
      summary: 'Emergency room triage for chest pain and shortness of breath.',
      workflowType: 'interpretation',
      tags: ['interpretation', 'telephonic'],
      minutes: 20,
      hoursAgo: 3,
    },
    {
      id: 'mock-scribe-jennifer',
      patientId: 'mock-jennifer-thompson',
      patientName: 'Jennifer Thompson',
      language: 'English',
      summary:
        'Post-operative care instructions after laparoscopic cholecystectomy.',
      workflowType: 'medical_scribing',
      tags: ['scribe'],
      minutes: 22,
      hoursAgo: 4,
    },
    {
      id: 'mock-interpreter-sophie',
      patientId: 'mock-sophie-dubois',
      patientName: 'Sophie Dubois',
      language: 'French',
      summary:
        'Follow-up appointment for diabetes management and medication adjustment.',
      workflowType: 'interpretation',
      tags: ['interpretation'],
      minutes: 17,
      hoursAgo: 5,
    },
    {
      id: 'mock-interpreter-ahmed',
      patientId: 'mock-ahmed-hassan',
      patientName: 'Ahmed Hassan',
      language: 'Arabic',
      summary:
        'Pre-operative consultation for knee surgery and post-operative planning.',
      workflowType: 'interpretation',
      tags: ['interpretation'],
      minutes: 19,
      hoursAgo: 6,
    },
    {
      id: 'mock-interpreter-carlos',
      patientId: 'mock-carlos-silva',
      patientName: 'Carlos Silva',
      language: 'Portuguese',
      summary: 'Mental health assessment and counseling session.',
      workflowType: 'interpretation',
      tags: ['interpretation'],
      minutes: 21,
      hoursAgo: 7,
    },
  ];

  return rows.map(row => {
    const end = new Date(now.getTime() - row.hoursAgo * 60 * 60 * 1000);
    const start = new Date(end.getTime() - row.minutes * 60 * 1000);
    const conversation = {
      id: row.id,
      user_id: 'mock-superadmin',
      patient_id: row.patientId,
      patient_name: row.patientName,
      minutes: row.minutes,
      start_time: start,
      end_time: end,
      summary: row.summary,
      tags: row.tags,
      workflow_type: row.workflowType,
      language: row.language,
    };
    return {
      id: `implicit:${row.id}`,
      isSynthetic: true,
      company_id: 'mock-ehr-company',
      patient_id: row.patientId,
      patient_name: row.patientName,
      ehr_patient_id:
        row.patientId === 'mock-maria-garcia'
          ? 'ECW-100245'
          : row.patientId === 'mock-james-wilson'
            ? 'ECW-100246'
            : null,
      status: 'closed',
      has_open_session: false,
      workflow_type: row.workflowType,
      language: row.language,
      summary: row.summary,
      start_time: start,
      end_time: end,
      conversations: [conversation],
      sessions: [conversation],
    };
  });
}

export function getMockChartingQueue(now: Date = new Date()): ChartingQueue {
  const rows = [
    [
      'mock_scribe_0',
      'Robert Mitchell',
      'English',
      'Emergency evaluation for chest pain and shortness of breath.',
      26,
      'needs_note',
      1,
    ],
    [
      'mock_interpretation_0',
      'María García',
      'Spanish',
      'Consultation for chronic back pain and mobility issues.',
      15,
      'draft',
      2,
    ],
    [
      'mock_interpretation_1',
      'Wei Chen',
      'Mandarin',
      'Emergency room triage for chest pain and shortness of breath.',
      20,
      'needs_note',
      3,
    ],
    [
      'mock_scribe_1',
      'Jennifer Thompson',
      'English',
      'Post-operative care instructions after laparoscopic cholecystectomy.',
      22,
      'needs_repush',
      4,
    ],
    [
      'mock_interpretation_2',
      'Sophie Dubois',
      'French',
      'Diabetes management and medication adjustment follow-up.',
      17,
      'draft',
      5,
    ],
    [
      'mock_interpretation_3',
      'Ahmed Hassan',
      'Arabic',
      'Pre-operative consultation for knee surgery and post-operative planning.',
      19,
      'needs_note',
      28,
    ],
  ] as const;
  const items = rows.map(
    ([
      conversationId,
      patientName,
      language,
      summary,
      minutes,
      status,
      hoursAgo,
    ]) => {
      const normalized = patientName
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
      const start = new Date(now.getTime() - hoursAgo * 60 * 60 * 1000);
      return {
        conversationId,
        modality: 'in_person' as const,
        workflowType: conversationId.includes('scribe')
          ? 'medical_scribing'
          : 'interpretation',
        patientName,
        patientId: `mock-${normalized.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`,
        ehrPatientId: normalized === 'maria garcia' ? 'ECW-100245' : null,
        startTime: start.toISOString(),
        minutes,
        templateType: 'soap',
        summary,
        mrn: normalized === 'maria garcia' ? '100245' : null,
        language,
        status,
        noteGeneratedAt:
          status === 'draft' || status === 'needs_repush'
            ? start.toISOString()
            : null,
        ehrPushedAt: null,
        completedAt: null,
      };
    },
  );
  return {
    date: now.toISOString().slice(0, 10),
    today: items.slice(0, 5),
    backlog: items.slice(5),
    pendingCount: items.length,
  };
}

export const MOCK_EHR_STATIC_NOTE = Object.freeze({
  templateId: 'mock-progress-note',
  templateName: 'Progress Note',
  sections: [
    {
      sectionId: 'mock-subjective',
      key: 'subjective',
      title: 'Subjective',
      content:
        'Patient reports fasting glucose between 140 and 180 mg/dL with increased fatigue.',
      order: 0,
    },
    {
      sectionId: 'mock-assessment',
      key: 'assessment',
      title: 'Assessment',
      content: 'Type 2 diabetes mellitus without complication (E11.9).',
      order: 1,
    },
    {
      sectionId: 'mock-plan',
      key: 'plan',
      title: 'Plan',
      content:
        'Continue metformin, reinforce nutrition goals, and repeat hemoglobin A1c in three months.',
      order: 2,
    },
  ],
});

const mockSectionContent = (sectionName: string): string => {
  const normalized = sectionName.trim().toLowerCase();
  if (normalized.includes('chief complaint')) {
    return 'Follow-up for type 2 diabetes and increased fatigue.';
  }
  if (normalized.includes('history of present illness')) {
    return 'Maria Garcia reports fasting glucose between 140 and 180 mg/dL with increased fatigue. She denies chest pain, shortness of breath, dizziness, or symptomatic hypoglycemia.';
  }
  if (normalized.includes('review of systems')) {
    return 'Constitutional: Positive for fatigue.\nCardiovascular: Denies chest pain.\nRespiratory: Denies shortness of breath.\nNeurologic: Denies dizziness.';
  }
  if (
    normalized.includes('exam') ||
    normalized.includes('objective') ||
    normalized.includes('physical')
  ) {
    return 'General: Alert and comfortable.\nRespiratory: Normal work of breathing at rest.\nCardiovascular: Regular rate and rhythm.';
  }
  if (normalized.includes('assessment')) {
    return '1. Type 2 diabetes mellitus without complications - E11.9\n2. Fatigue, unspecified - R53.83';
  }
  if (normalized.includes('plan') || normalized.includes('treatment')) {
    return '[E11.9] Type 2 diabetes mellitus\n- Continue metformin as prescribed.\n- Reinforce nutrition and activity goals.\n- Repeat hemoglobin A1c in 3 months.';
  }
  return 'Reviewed during this simulated visit; no additional concerns were documented.';
};

export function buildMockEhrNote(template: any) {
  const templateSections = Array.isArray(template?.sections)
    ? template.sections
    : [];
  const sections = Object.fromEntries(
    templateSections.map((section: any, index: number) => {
      const name =
        typeof section === 'string'
          ? section
          : section?.name || section?.title || `Section ${index + 1}`;
      return [name, mockSectionContent(name)];
    }),
  );
  const rawTranscription = MOCK_EHR_TRANSCRIPT.map(
    message => `${message.speaker}: ${message.text}`,
  ).join('\n');

  return {
    id: 'mock-ehr-note',
    templateId: template?.id || 'soap',
    templateName: template?.name || 'Progress Note',
    sections,
    sectionMeta: {},
    rawTranscription,
    generatedAt: new Date(),
    isFromHistory: false,
    documentContext: null,
    codeProvenance: [],
  };
}

const appointmentRow = (
  patient: (typeof MOCK_EHR_PATIENTS)[number],
  startIso: string,
  id: string,
  reason: string,
  providerId: string,
  providerName: string,
): ScheduleViewRow => ({
  kind: 'appointment',
  matchKind: 'ehrOnly',
  time: {startIso, endIso: null},
  patient: {
    patientId: null,
    ehrPatientId: patient.id,
    name: patient.displayName,
    dob: patient.dob,
    language: patient.language,
    mrn: patient.mrn,
    source: 'ehr',
  },
  appointment: {
    id,
    providerId,
    providerName,
    status: 'scheduled',
    reason,
    facility: 'Opalite Family Medicine',
  },
});

export function getMockEhrSchedule(
  now: Date = new Date(),
  selection: {all?: boolean; keys?: string[]} | null = null,
): ScheduleViewResponse {
  const date = new Date(now);
  date.setHours(0, 0, 0, 0);
  const appointments = [
    [
      0,
      'mock-appt-maria',
      'Diabetes follow-up',
      'mock-provider-anderson',
      'Dr. Anderson',
    ],
    [
      1,
      'mock-appt-james',
      'Annual wellness visit',
      'mock-provider-anderson',
      'Dr. Anderson',
    ],
    [
      2,
      'mock-appt-carmen',
      'Chronic back pain follow-up',
      'mock-provider-anderson',
      'Dr. Anderson',
    ],
    [
      3,
      'mock-appt-sophie',
      'Post-operative check',
      'mock-provider-anderson',
      'Dr. Anderson',
    ],
    [
      4,
      'mock-appt-daniel',
      'Lab review',
      'mock-provider-anderson',
      'Dr. Anderson',
    ],
    [
      5,
      'mock-appt-lucia',
      'Hypertension follow-up',
      'mock-provider-anderson',
      'Dr. Anderson',
    ],
    [
      6,
      'mock-appt-amina',
      'Medication review',
      'mock-provider-patel',
      'Dr. Patel',
    ],
    [
      7,
      'mock-appt-wei',
      'New patient consultation',
      'mock-provider-chen',
      'Dr. Chen',
    ],
  ] as const;
  const selectedProviderIds = new Set(
    (selection?.keys || [])
      .filter(key => key.startsWith('id:'))
      .map(key => key.slice(3)),
  );
  const visibleAppointments = appointments.filter(([, , , providerId]) => {
    if (selection?.all) return true;
    if (selectedProviderIds.size) return selectedProviderIds.has(providerId);
    return providerId === 'mock-provider-anderson';
  });
  const upcoming = visibleAppointments.map(
    ([patientIndex, id, reason, providerId, providerName], index) =>
      appointmentRow(
        MOCK_EHR_PATIENTS[patientIndex],
        new Date(now.getTime() + (45 + index * 90) * 60 * 1000).toISOString(),
        id,
        reason,
        providerId,
        providerName,
      ),
  );
  const completed = getMockEhrRecentEncounters(now)
    .slice(0, 5)
    .map((encounter: any): ScheduleViewRow => {
      const conversation = encounter.conversations[0];
      const isScribe = encounter.workflow_type === 'medical_scribing';
      return {
        kind: 'encounter',
        matchKind: 'linked',
        time: {
          startIso: conversation.start_time.toISOString(),
          endIso: conversation.end_time.toISOString(),
        },
        patient: {
          patientId: encounter.patient_id,
          ehrPatientId: encounter.ehr_patient_id,
          name: encounter.patient_name,
          dob: null,
          language: encounter.language,
          mrn: null,
          source: 'inline',
        },
        encounter: {
          encounterId: encounter.id,
          sessionIds: [conversation.id],
          status: 'closed',
          noteStatus: isScribe ? 'generated' : 'none',
          totalMinutes: conversation.minutes,
          summary: encounter.summary,
          tags: conversation.tags,
          providerUserIds: [conversation.user_id],
          contributors: [{userId: conversation.user_id, name: 'Dr. Anderson'}],
        },
      };
    });
  const scopeMode = selection?.all
    ? 'all'
    : selectedProviderIds.size
      ? 'manual'
      : 'auto';
  const matchedProviderId = selection?.all
    ? null
    : [...selectedProviderIds][0] || 'mock-provider-anderson';
  return {
    date: date.toISOString().slice(0, 10),
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    ehr: {enabled: true, type: 'mock'},
    providerScope: {
      mode: scopeMode,
      matchedProviderId,
      providers: [
        {
          key: 'id:mock-provider-anderson',
          name: 'Dr. Anderson',
          count: 6,
        },
        {
          key: 'id:mock-provider-patel',
          name: 'Dr. Patel',
          count: 1,
        },
        {
          key: 'id:mock-provider-chen',
          name: 'Dr. Chen',
          count: 1,
        },
      ],
    },
    sections: {
      incomplete: [],
      completed,
      noShows: [],
      upcoming,
      later: [],
    },
  };
}

export const MOCK_EHR_ENCOUNTERS = Object.freeze([
  {
    id: 'mock-encounter-maria',
    encounterId: 'ENC-MARIA-TODAY',
    encounterDate: '2026-01-15',
    encounterTime: '10:30 AM',
    encounterReason: 'Diabetes follow-up',
    providerName: 'Dr. Anderson',
    locked: false,
    __mockEhr: true,
  },
  {
    id: 'mock-encounter-james',
    encounterId: 'ENC-JAMES-TODAY',
    encounterDate: '2026-01-08',
    encounterTime: '09:00 AM',
    encounterReason: 'Annual wellness visit',
    providerName: 'Dr. Anderson',
    locked: false,
    __mockEhr: true,
  },
]);

export function isMockEhrEnabled(
  userData: any,
  mockEhrMode?: boolean,
): boolean {
  return (
    userData?.role === 'superadmin' &&
    Boolean(
      mockEhrMode ?? userData?.superAdminPreferences?.mockEhrMode ?? false,
    )
  );
}
